<template>
    <ui-container class="py-8" v-if="this.form">
        <ui-header
            :title="editMode ? 'Editing PPE Register - '+this.ppeRegister.formattedDate : 'Create new PPE Register'"
            :subtitle="editMode ? 'ID #'+this.ppeRegister.id : null">
            <template #actions>
                <ui-button-new icon="fas fa-arrow-left" @click="finished()" v-if="editMode">Done</ui-button-new>
            </template>
        </ui-header>
        <div class="grid gap-6 grid-cols-12 py-6">
            <ui-date-picker label="PPE Register Date" name="date" v-model="form.date" class="xl:col-span-6"
                            placeholder="What is the date for this register?"
                            :error="errors.date"
                            @input="save"></ui-date-picker>
        </div>
        <label class="my-0 py-0 mt-0">People</label>
        <div
            class="flex mt-1.5 border-t border-b border-gray-200 bg-gray-50 text-xs text-gray-500 font-semibold draggable">
            <div class="w-6 flex-shrink-0"></div>
            <div class="flex-1 border-l border-r border-gray-200 px-2 py-1" style="min-width: 55px;">Name</div>
            <element-size-sync ref="sync1" :other-elements="() => getPersonElementsFor('hardhat')">
                <div class="border-r border-gray-200 p-0" style="min-width: 32px">
                    <rotate>
                        <autoresizable-input
                            measure="p-1 m-0 border-none text-xs font-semibold text-gray-500"
                            :min="32">
                            <page-cell name="name" value="Hard Hat" disabled
                                       class="border-none shadow-none bg-transparent text-xs text-gray-500 font-semibold p-1 m-0">
                            </page-cell>
                        </autoresizable-input>
                    </rotate>
                </div>
            </element-size-sync>
            <element-size-sync ref="sync2" :other-elements="() => getPersonElementsFor('highVisibilityVest')">
                <div class="border-r border-gray-200 p-0" style="min-width: 32px">
                    <rotate>
                        <autoresizable-input
                            measure="p-1 m-0 border-none text-xs font-semibold text-gray-500"
                            :min="32">
                            <page-cell name="name" value="Hi-Vis" disabled
                                       class="border-none shadow-none bg-transparent text-xs text-gray-500 font-semibold p-1 m-0">
                            </page-cell>
                        </autoresizable-input>
                    </rotate>
                </div>
            </element-size-sync>
            <element-size-sync ref="sync3" :other-elements="() => getPersonElementsFor('boots')">
                <div class="border-r border-gray-200 p-0" style="min-width: 32px">
                    <rotate>
                        <autoresizable-input
                            measure="p-1 m-0 border-none text-xs font-semibold text-gray-500"
                            :min="32">
                            <page-cell name="name" v-bind="form.boots" value="Boots" disabled
                                       class="border-none shadow-none bg-transparent text-xs text-gray-500 font-semibold p-1 m-0">
                            </page-cell>
                        </autoresizable-input>
                    </rotate>
                </div>
            </element-size-sync>
            <element-size-sync ref="sync4" :other-elements="() => getPersonElementsFor('goggles')">
                <div class="border-r border-gray-200 p-0" style="min-width: 32px">
                    <rotate>
                        <autoresizable-input
                            measure="p-1 m-0 border-none text-xs font-semibold text-gray-500"
                            :min="32">
                            <page-cell name="name" value="Goggles" disabled
                                       class="border-none shadow-none bg-transparent text-xs text-gray-500 font-semibold p-1 m-0">
                            </page-cell>
                        </autoresizable-input>
                    </rotate>
                </div>
            </element-size-sync>
            <element-size-sync ref="sync5" :other-elements="() => getPersonElementsFor('gloves')">
                <div class="border-r border-gray-200 p-0" style="min-width: 32px">
                    <rotate>
                        <autoresizable-input
                            measure="p-1 m-0 border-none text-xs font-semibold text-gray-500"
                            :min="32">
                            <page-cell name="name" value="Gloves" disabled
                                       class="border-none overflow-hidden shadow-none bg-transparent text-xs text-gray-500 font-semibold p-1 m-0">
                            </page-cell>
                        </autoresizable-input>
                    </rotate>
                </div>
            </element-size-sync>
            <element-size-sync ref="sizesync" :other-elements="getPersonElements"
                               v-for="(equipment) in form.otherEquipment" :key="equipment.id">
                <div class="border-r border-gray-200 px-0 py-0" style="min-width: 32px" ref="equipmentName">
                    <rotate>
                        <autoresizable-input :post-resize="resize"
                                             measure="p-1 m-0 border-none text-xs font-semibold text-gray-500"
                                             :min="32">
                            <page-cell name="name" v-model="equipment.name" @input="save()" ref="equipmentCell"
                                       class="border-none overflow-hidden shadow-none bg-transparent text-xs text-gray-500 font-semibold p-1 m-0">
                            </page-cell>
                        </autoresizable-input>
                    </rotate>
                </div>
            </element-size-sync>
            <div class="w-20 border-r border-gray-200 px-2 py-1">Signature</div>
            <div class="w-6 flex-shrink-0 ml-2 mt-1">
                <ui-dropdown menu-class="p-3 w-72">
                    <template #default="{ toggle }">
                        <button @click="toggle"
                                class="w-5 h-5 flex items-center justify-center text-gray-300 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:z-10 relative rounded">
                            <i class="fa fa-ellipsis-h fa-fw"></i>
                        </button>
                    </template>
                    <template #menu="{ close }">
                        <ui-dropdown-item icon="fas fa-adjust" @click="addColumn();close();">Add Other Equipment
                        </ui-dropdown-item>
                        <ui-dropdown-divider v-if="form.otherEquipment.length"></ui-dropdown-divider>
                        <ui-dropdown-item icon="fas fa-trash" danger v-for="(equipment) in form.otherEquipment"
                                          :key="equipment.id"
                                          @click="removeColumn(equipment);close();">Remove
                            "{{ equipment.name }}"
                        </ui-dropdown-item>

                    </template>
                </ui-dropdown>
            </div>
        </div>
        <draggable :list="currentPeople" draggable=".draggable" handle=".handle" group="people" @change="save">
            <ppe-register-person v-for="(person,index) in currentPeople"
                                 :key="person.id"
                                 :person="person"
                                 :other-equipment="form.otherEquipment"
                                 ref="people"
                                 :contacts="contacts"
                                 @updatePerson="save"
                                 @removePerson="removePerson"
            ></ppe-register-person>
        </draggable>
        <!-- New Person Item. -->
        <div class="flex border-b -mb-px border-gray-200">
            <div class="w-6 flex-shrink-0">
                <div class="px-2 py-2 w-5 h-5 flex people-center">
                    <i class="fa fa-plus text-xs text-gray-300"></i>
                </div>
            </div>
            <div class="flex-1 border-l border-r border-gray-200 px-0 py-0">
                <textarea
                    placeholder="Add a new person..."
                    class="px-2 py-1 b-0 border-0 hover:z-10 focus:z-20 resize-none text-sm m-0 border-none shadow-none relative"
                    type="text"
                    rows="1"
                    v-model="newPerson.name"
                    @input="addPersonLine"
                ></textarea>
            </div>
            <div class="w-6 ml-2 mt-1">
            </div>
        </div>
        <div class="grid gap-6 grid-cols-12 py-6">
            <package-contact-search
                class="xl:col-span-6 col-span-12"
                #default="{ open, query, setQuery }"
                @selected:contact="givenBySelected"
                :top="true"
                :contacts="contacts"
            >
                <ui-input label="Given By" name="givenByName" v-model="form.givenByName" placeholder=""
                          class="xl:col-span-6 col-span-12" :error="form.errors.get('givenByName')"
                          @input="setQuery(form.givenByName);open();save()"
                          @focus="setQuery(form.givenByName);open();"
                          :disabled="!!ppeRegister.signature"
                ></ui-input>
            </package-contact-search>
            <ui-input label="Position" name="givenByPosition" v-model="form.givenByPosition" placeholder=""
                      class="xl:col-span-6" :error="form.errors.get('givenByPosition')" @input="save" :disabled="!!ppeRegister.signature">

            </ui-input>
            <div class="lg:col-span-2" v-if="ppeRegister.signature">
                <dt class="text-sm font-medium text-gray-500">Signed</dt>
                <dd class="mt-1 text-sm text-gray-900 w-full">
                    <img :src="ppeRegister.signature" style="height:48px">
                </dd>
            </div>
        </div>

        <ui-data-grid-item wide class="mt-6 flex" v-if="!editMode">
            <div class="flex flex-col md:flex-row justify-end flex-wrap">
                <ui-button-new v-if="editMode" @click="edit(false)" class="ml-3">Cancel</ui-button-new>
                <ui-button-new primary @click.prevent="form.submit()" :loading="form.busy" class="ml-3">Create
                </ui-button-new>
            </div>
        </ui-data-grid-item>

        <ui-data-grid class="mt-6" v-if="editMode">
            <ui-data-grid-item label="Created By">{{ ppeRegister.createdBy }} on {{
                    ppeRegister.createdOn
                }}
            </ui-data-grid-item>
            <ui-data-grid-item label="Last Updated By" v-if="ppeRegister.lastUpdatedBy">{{ ppeRegister.lastUpdatedBy }}
                on {{
                    ppeRegister.lastUpdatedOn
                }}
            </ui-data-grid-item>
        </ui-data-grid>

    </ui-container>

</template>

<script>
import draggable from 'vuedraggable'
import {PPERegister} from '@models'
import UiDataGridItem from "../../ui/UiDataGridItem.vue";
import UiButtonNew from "../../ui/UiButtonNew.vue";
import UiInput from "../../ui/UiInput.vue";
import PpeRegisterPerson from "./PpeRegisterPerson.vue";
import UiDatePicker from "../../ui/UiDatePicker.vue";
import UiHeader from "../../ui/UiHeader.vue";
import UiContainer from "../../ui/UiContainer.vue";
import PackageContactSearch from "../../contacts/PackageContactSearch.vue";
import {uuid} from "../../../utils";
import moment from "moment/moment";
import AutoresizableInput from "../../ui/AutoresizableInput";
import PageCell from "../../ui/PageCell.vue";
import ElementSizeSync from "../../ui/ElementSizeSync";
import UiDataGrid from "../../ui/UiDataGrid.vue";
import Rotate from "../../ui/Rotate.vue";
import UiDropdownItem from "../../ui/UiDropdownItem.vue";

export default {
    components: {
        UiDropdownItem,
        Rotate,
        UiDataGrid,
        ElementSizeSync,
        PageCell,
        AutoresizableInput,
        PackageContactSearch,
        UiContainer,
        UiHeader,
        UiDatePicker,
        PpeRegisterPerson,
        UiInput,
        UiButtonNew,
        UiDataGridItem,
        draggable,
    },
    props: {
        item: Object,
        edit: Function,
        addItem: Function,
        contacts: Object,
        packageReference: null,
        packageName: null,
    },
    data() {
        return {
            ppeRegister: null,
            currentPeople: [],
            newPerson: {
                name: null
            },
            form: null,
            errors: {
                date: null
            },
            saving: false,
            saveAgain: false,
        }
    },
    methods: {
        resize(e) {
            this.$nextTick(() => {
                this.$refs.sync1.resizeElements()
                this.$refs.sync2.resizeElements()
                this.$refs.sync3.resizeElements()
                this.$refs.sync4.resizeElements()
                this.$refs.sync5.resizeElements()
                this.$refs.sizesync?this.$refs.sizesync.map((i) => i.resizeElements()):false
            })
        },
        getPersonElementsFor(ref) {
            if (this.$refs.people) {
                return this.$refs.people.map((i) => {
                    return i.$refs[ref]
                })
            }
            return [];
        },
        getPersonElements(e) {
            if ((this.$refs.equipmentName) && (this.$refs.people)) {
                let index = this.$refs.equipmentName.findIndex((i) => i === e.$el);
                return this.$refs.people.map((i) => {
                    return i.$refs.otherEquipment[index]
                })
            }
            return [];
        },
        addColumn() {
            const id = uuid();
            this.form.otherEquipment.push({
                id: id,
                name: 'Equipment ' + (this.form.otherEquipment.length + 1),
            })
            this.currentPeople.map((i) => i.selectedOtherEquipment[id] = false)
            this.save();
            this.$nextTick(() => {
                const inputField = this.$refs.equipmentCell[this.$refs.equipmentCell.length - 1].$el
                let val = inputField.value;
                inputField.focus();
                inputField.value = val;
            })
        },
        removeColumn(equipment) {
            this.form.otherEquipment = this.form.otherEquipment.filter((i) => i !== equipment)
            this.currentPeople.forEach((i) => delete i.selectedOtherEquipment[equipment.id])
            this.save();
        },
        addPersonLine() {
            if (!this.newPerson.name) return
            this.currentPeople.push({
                id: uuid(),
                name: this.newPerson.name,
                order: this.currentPeople.length,
                selectedOtherEquipment: this.form.otherEquipment.reduce((o, b) => ({...o, [b.id]: false}), {})
            });
            this.save();
            this.newPerson.name = ''
            this.resize()
            this.$nextTick(() => {
                const inputField = this.$refs.people[this.$refs.people.length - 1].$refs.name
                let val = inputField.value;
                inputField.focus();
                inputField.value = val;
            })
        },
        removePerson(e) {
            this.currentPeople = this.currentPeople.filter(person => person.id !== e.id);
            this.save();
        },
        save() {
            if (this.editMode) {
                if (this.saving) {
                    this.saveAgain = true;
                }
                this.saving = true
                this.form.submit()
            }
        },
        finished() {
            let sorter = (items) => {
                items.sort((a, b) => (a.date < b.date) ? 1 : ((b.date < a.date) ? -1 : 0));
            };
            this.edit(false, sorter)
        },
        givenBySelected(e) {
            this.form.givenByName = e.name
            this.save();
        },
    },
    computed: {
        editMode() {
            return !!this.item
        },
    },
    async mounted() {
        if (this.editMode) {
            this.ppeRegister = await PPERegister.find(this.packageReference, this.item.id)
        } else {
            this.ppeRegister = {date: moment().format('YYYY-MM-DD'), people: [], otherEquipment: []}
        }
        this.currentPeople = this.ppeRegister.people
        this.form = new Form(this.editMode ? this.ppeRegister.getFormAttributes() : this.ppeRegister, async (form) => {
            this.saving = true;
            do {
                this.saveAgain = false
                let data = form.export();
                if (!data.date) {
                    this.errors.date = "The date field is required. "
                    return;
                }
                if (!data.package_id && this.packageId) {
                    data.package_id = this.packageId;
                }
                data.packageReference = this.packageReference;
                data.packageName = this.packageName;
                data.people = this.currentPeople ? this.currentPeople : [];
                let sorter = (items) => {
                    items.sort((a, b) => (a.date < b.date) ? 1 : ((b.date < a.date) ? -1 : 0));
                };
                if (this.editMode) {
                    await this.item.update(data).then(() =>
                        Object.assign(this.ppeRegister, this.item)
                    )

                    this.item.people.forEach((item, key) => {
                        this.currentPeople[key].id = item.id
                        this.currentPeople[key].order = item.order
                    })
                } else {
                    const ppeRegister = await PPERegister.create(this.packageReference, uuid(), data)

                    this.addItem(ppeRegister, 0, sorter);
                    form.reset()
                }
            } while (this.saveAgain)
            this.saving = false;
        })

    }
}
</script>
